import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '@/i18n/locale-context';
import { readConsent, writeConsent, type ConsentState } from '@/lib/consent';
import { Button } from '@/components/ui';

/**
 * The measurement notice — phases.md Phase 16, "a lightweight cookie/consent notice
 * appropriate to the jurisdiction".
 *
 * Appropriate to *this* jurisdiction means the DPDPA 2023: clear notice, a real choice,
 * and withdrawal as easy as consent. It does not mean a full-screen European cookie wall
 * with a "manage 812 partners" link, and building one would misdescribe what this site
 * does — there are no partners, no advertising identifiers, and no cookie behind this
 * notice at all.
 *
 * Three deliberate choices:
 *
 * - **A bar at the bottom, not a modal.** It never covers the story, and a reader who
 *   ignores it can read the site. A news site that blocks the news until you agree to be
 *   counted has its priorities inverted.
 * - **Decline is the same button as accept.** Same size, same weight, same prominence.
 *   A choice presented unequally is not consent, whatever the statute allows.
 * - **It renders only after mount.** The server does not know what this reader chose
 *   last time, so rendering it into the HTML would flash a notice at everyone who has
 *   already answered — and hydration would disagree with the markup.
 */
export function ConsentNotice() {
  const { t, path } = useLocale();
  const [state, setState] = useState<ConsentState>('accepted');

  useEffect(() => {
    setState(readConsent());
  }, []);

  if (state !== 'undecided') return null;

  const decide = (choice: 'accepted' | 'declined') => {
    writeConsent(choice);
    setState(choice);
  };

  return (
    <div
      // `region` with a label rather than `dialog`: nothing is blocked behind it, and
      // trapping focus in a bar the reader is allowed to ignore would be a lie to a
      // screen reader about what the page is doing.
      role="region"
      aria-label={t('consent.title')}
      className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 px-gutter py-3 shadow-shell backdrop-blur"
    >
      <div className="mx-auto flex max-w-shell flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-snug text-ink">
          {t('consent.body')}{' '}
          <Link
            to={path('privacy')}
            className="font-semibold text-red underline-offset-4 hover:underline"
          >
            {t('footer.privacy')}
          </Link>
        </p>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" variant="secondary" onClick={() => decide('declined')}>
            {t('consent.decline')}
          </Button>
          <Button size="sm" variant="primary" onClick={() => decide('accepted')}>
            {t('consent.accept')}
          </Button>
        </div>
      </div>
    </div>
  );
}
